import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, CheckCircle2, XCircle, AlertCircle, Clock, Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { projectId } from '../utils/supabase/info';

interface PendingCI {
  id: string;
  name: string;
  description: string;
  category: string;
  priority: string;
  reasoning: string;
  estimatedHours: number;
  suggestedAt: string;
}

interface CIApprovalModalProps {
  companyId: string;
  companyName: string;
  pendingCIs: PendingCI[];
  accessToken: string;
  onClose: () => void;
  onComplete: () => void;
}

export function CIApprovalModal({ 
  companyId, 
  companyName, 
  pendingCIs, 
  accessToken, 
  onClose, 
  onComplete 
}: CIApprovalModalProps) {
  const [decisions, setDecisions] = useState<Record<string, { approved: boolean; rejectionReason?: string }>>({});
  const [loading, setLoading] = useState(false);

  const handleToggleApproval = (ciId: string) => {
    setDecisions(prev => {
      const current = prev[ciId];
      if (current?.approved) {
        // Was approved, now reject
        return { ...prev, [ciId]: { approved: false, rejectionReason: '' } };
      } else {
        // Was rejected or unset, now approve
        return { ...prev, [ciId]: { approved: true } };
      }
    });
  };

  const handleSetRejectionReason = (ciId: string, reason: string) => {
    setDecisions(prev => ({
      ...prev,
      [ciId]: { approved: false, rejectionReason: reason }
    }));
  };

  const handleApproveAll = () => {
    const allApproved = pendingCIs.reduce((acc, ci) => {
      acc[ci.id] = { approved: true };
      return acc;
    }, {} as Record<string, { approved: boolean }>);
    setDecisions(allApproved);
  };

  const handleRejectAll = () => {
    const allRejected = pendingCIs.reduce((acc, ci) => {
      acc[ci.id] = { approved: false, rejectionReason: 'Rejected in bulk' };
      return acc;
    }, {} as Record<string, { approved: boolean; rejectionReason: string }>);
    setDecisions(allRejected);
  };

  const handleSubmit = async () => {
    // Ensure all items have decisions
    const allDecisions = pendingCIs.map(ci => ({
      id: ci.id,
      approved: decisions[ci.id]?.approved ?? false,
      rejectionReason: decisions[ci.id]?.rejectionReason || undefined,
    }));

    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/approve-cis/${companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ decisions: allDecisions }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to process approvals');
      }

      const data = await response.json();
      console.log('[CI APPROVAL] Success:', data);

      onComplete();
      onClose();
    } catch (error) {
      console.error('[CI APPROVAL] Error:', error);
      alert('Failed to process approvals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const approvedCount = Object.values(decisions).filter(d => d.approved).length;
  const rejectedCount = Object.values(decisions).filter(d => !d.approved).length;
  const decidedCount = Object.keys(decisions).length;

  const categoryColors: Record<string, string> = {
    workflow: 'bg-blue-100 text-blue-700',
    security: 'bg-red-100 text-red-700',
    integration: 'bg-purple-100 text-purple-700',
    data_model: 'bg-green-100 text-green-700',
    automation: 'bg-orange-100 text-orange-700',
    reporting: 'bg-teal-100 text-teal-700',
    communication: 'bg-pink-100 text-pink-700',
    user_management: 'bg-indigo-100 text-indigo-700',
    documentation: 'bg-gray-100 text-gray-700',
  };

  const priorityColors: Record<string, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700',
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-2xl">AI-Suggested Configuration Items</h2>
          </DialogTitle>
          <p className="text-gray-600">
            Review {pendingCIs.length} configuration items suggested for <span className="font-medium">{companyName}</span>
          </p>

          {/* Bulk Actions */}
          <div className="flex items-center gap-3 mt-4">
            <Button onClick={handleApproveAll} variant="outline" size="sm" disabled={loading}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approve All
            </Button>
            <Button onClick={handleRejectAll} variant="outline" size="sm" disabled={loading}>
              <XCircle className="w-4 h-4 mr-2" />
              Reject All
            </Button>
            {decidedCount > 0 && (
              <div className="ml-auto text-sm text-gray-600">
                {approvedCount} approved · {rejectedCount} rejected
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 overflow-y-auto p-6 space-y-4">
          {pendingCIs.map((ci) => {
            const decision = decisions[ci.id];
            const isApproved = decision?.approved;
            const isRejected = decision && !decision.approved;

            return (
              <Card
                key={ci.id}
                className={`transition-all ${
                  isApproved
                    ? 'border-green-300 bg-green-50'
                    : isRejected
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Header */}
                <CardHeader className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        checked={isApproved || false}
                        onCheckedChange={() => handleToggleApproval(ci.id)}
                      />
                      <h3 className="font-medium">{ci.name}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={categoryColors[ci.category] || 'bg-gray-100 text-gray-700'}>
                        {ci.category.replace('_', ' ')}
                      </Badge>
                      <Badge className={priorityColors[ci.priority] || 'bg-gray-100 text-gray-700'}>
                        {ci.priority}
                      </Badge>
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        ~{ci.estimatedHours}h
                      </span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  {isApproved && (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Approved
                    </Badge>
                  )}
                  {isRejected && (
                    <Badge className="bg-red-100 text-red-700">
                      <XCircle className="w-3 h-3 mr-1" />
                      Rejected
                    </Badge>
                  )}
                </CardHeader>

                {/* Description */}
                <CardContent className="text-sm text-gray-700 mb-3">{ci.description}</CardContent>

                {/* AI Reasoning */}
                <CardDescription className="bg-purple-50 border border-purple-200 rounded-md p-3 mb-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-purple-900 mb-1">AI Analysis:</p>
                      <p className="text-sm text-purple-800">{ci.reasoning}</p>
                    </div>
                  </div>
                </CardDescription>

                {/* Rejection Reason (if rejected) */}
                {isRejected && (
                  <div className="mt-3">
                    <Label className="text-sm text-gray-700 mb-2 block">
                      Rejection Reason (optional):
                    </Label>
                    <Textarea
                      value={decision?.rejectionReason || ''}
                      onChange={(e) => handleSetRejectionReason(ci.id, e.target.value)}
                      placeholder="Why are you rejecting this item?"
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                )}
              </Card>
            );
          })}
        </ScrollArea>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {decidedCount === 0 && 'Select items to approve or reject'}
            {decidedCount > 0 && decidedCount < pendingCIs.length && `${decidedCount} of ${pendingCIs.length} items decided`}
            {decidedCount === pendingCIs.length && 'All items decided'}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || decidedCount === 0}
            >
              {loading ? 'Processing...' : `Submit Decisions (${decidedCount})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}